import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import Lightbox from "./Lightbox";

const imgUrl = (file: string) =>
  `${process.env.REACT_APP_API_URL}/img/tours/${file}`;

type Props = {
  /** Cover first, then additional images. */
  photos: string[];
  name: string;
};

/**
 * Image mosaic: a large lead image plus up to four supporting tiles. Any tile
 * opens a full-screen lightbox (todo #18); the 5th tile carries a "+N photos"
 * overlay when the tour has more images than the mosaic can show.
 */
const Gallery = ({ photos, name }: Props) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!photos.length) return null;
  const images = photos.map(imgUrl);
  const [lead, ...rest] = photos;
  const thumbs = rest.slice(0, 4);
  // How many images sit beyond the 5 the mosaic shows.
  const remaining = photos.length - (1 + thumbs.length);

  const open = (i: number) => setLightboxIndex(i);

  const tileClass =
    "group relative cursor-pointer overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-400";
  const imgClass =
    "h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]";

  return (
    <section className="mx-auto max-w-[1320px] px-6 md:px-12">
      <div className="grid gap-3 overflow-hidden rounded-[22px] md:h-[492px] md:grid-cols-[2fr_1fr_1fr] md:grid-rows-2">
        <button
          type="button"
          onClick={() => open(0)}
          aria-label={`${name} — open photo 1`}
          className={`${tileClass} md:row-span-2`}
        >
          <img
            src={images[0]}
            alt={name}
            crossOrigin="anonymous"
            className={`h-64 md:h-full ${imgClass}`}
          />
        </button>

        {thumbs.map((file, i) => {
          const idx = i + 1; // account for the lead image
          const isLastTile = i === thumbs.length - 1;
          const showOverflow = isLastTile && remaining > 0;
          return (
            <button
              type="button"
              key={file}
              onClick={() => open(showOverflow ? 1 + thumbs.length : idx)}
              aria-label={
                showOverflow
                  ? `View all ${photos.length} photos`
                  : `${name} — open photo ${idx + 1}`
              }
              className={tileClass}
            >
              <img
                src={imgUrl(file)}
                alt={`${name} ${i + 1}`}
                crossOrigin="anonymous"
                className={`h-40 md:h-full ${imgClass}`}
              />
              {showOverflow && (
                <span className="absolute inset-0 flex items-center justify-center bg-secondary-400/60 text-[15px] font-bold text-white">
                  + {remaining} photo{remaining > 1 ? "s" : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={images}
            name={name}
            startIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
